import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Tooltip} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {ToastService} from '@frontend/toast';
import {
  AttributeConsistencyStatus,
  AuditInfo,
  EvaluationSubCriteriaAttributeResponse,
  EvaluationSubCriteriaImportanceScore,
  TenderPhaseType,
} from '@frontend/shared-api';
import {SubCriteriaAttributeEvaluationService} from '../../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.service';
import {SubcriteriaAttributeSaveService} from '../../../../../../../shared/services/subcriteria-attribute-save.service';
import {SubcriteriaBadgeService} from '../../../../../../../shared/services/subcriteria-badge.service';
import {AttributeConsistencyService} from '../../../../../../../shared/services/attribute-consistency.service';
import {ModuleTab} from '../ec3-module-group/ec3-module-group.types';
import {isEC3} from '../../../../../../../shared/constants/evaluation-criteria.constants';
import {Ec3ModuleGroupComponent} from '../ec3-module-group/ec3-module-group.component';
import {
  EvaluationCriteriaTabVM,
  SubCriteriaTabVM,
} from '../step-6-1-evaluation-criteria-comparison.types';
import {SubCriteriaTabComponent} from '../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.component';
import {
  AttributeConsistencyChange,
  AttributeScoreSelection,
  AttributeScoreVM,
  SubCriteriaPanelVM,
} from '../../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.types';
import {SavedSubCriteriaScores} from './evaluation-criteria-tab.types';
import {logTabRender} from '../../../../../../../shared/tabs/tab-render-log.util';
import {TabStatusDirective} from '../../../../../../../shared/tabs/tab-status.directive';
import {TabsUnsavedRevealDirective} from '../../../../../../../shared/tabs/tabs-unsaved-reveal.directive';
import {ATTRIBUTE_CONSISTENCY_STATUS_COLORS} from '../../../../../../../shared/constants/attribute-consistency.constants';

@Component({
  selector: 'app-evaluation-criteria-tab',
  imports: [
    Card,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tooltip,
    TranslocoPipe,
    TranslateDirectusPipe,
    SubCriteriaTabComponent,
    Ec3ModuleGroupComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './evaluation-criteria-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationCriteriaTabComponent {
  private readonly evaluationService = inject(SubCriteriaAttributeEvaluationService);
  private readonly saveService = inject(SubcriteriaAttributeSaveService);
  private readonly badgeService = inject(SubcriteriaBadgeService);
  private readonly consistencyService = inject(AttributeConsistencyService);
  private readonly toast = inject(ToastService);

  readonly criteria = input.required<EvaluationCriteriaTabVM>();
  readonly projectId = input.required<number>();
  readonly disabled = input.required<boolean>();
  readonly ec3ModuleTabs = input<ModuleTab[]>([]);
  readonly showConsistency = input(false);

  readonly saved = output<SavedSubCriteriaScores>();
  readonly consistencyChanged = output<AttributeConsistencyChange>();
  readonly unsavedScoresChanged = output<boolean>();

  protected readonly phase = TenderPhaseType.RFP;
  protected readonly consistencyWarningColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.REVIEW_REQUIRED];
  protected readonly consistencyClarificationColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.CLARIFICATION_REQUIRED];

  protected readonly activeSubCriteriaTabIndex = signal(0);
  protected readonly lastSaveAuditInfo = signal<AuditInfo | null>(null);
  private readonly savingSubCriteriaId = signal<number | undefined>(undefined);
  private readonly unsavedScores = signal(
    new Map<string, EvaluationSubCriteriaImportanceScore | undefined>(),
  );

  protected readonly subCriteriaPanels = computed<SubCriteriaPanelVM[]>(() => {
    const unsavedScores = this.unsavedScores();
    const savingSubCriteriaId = this.savingSubCriteriaId();

    return this.criteria().subCriteria.map((subCriteria) => {
      const attributes = this.applyEdits(subCriteria, unsavedScores);

      return {
        id: subCriteria.id,
        evaluationCriteriaId: this.criteria().id,
        directusId: subCriteria.directusId,
        translations: subCriteria.translations,
        status: subCriteria.status,
        badges: this.badgeService.createBadgeListItems(attributes),
        hasUnsavedScores: attributes.some((attribute) => unsavedScores.has(attribute.directusId)),
        isSaveAllowed: true,
        isSaving: savingSubCriteriaId === subCriteria.id,
        unresolvedConsistencyCount: this.consistencyService.countUnresolved(attributes),
        clarificationConsistencyCount:
          this.consistencyService.countClarificationRequired(attributes),
      };
    });
  });

  protected readonly hasSubCriteria = computed<boolean>(() => {
    return this.subCriteriaPanels().length > 0;
  });

  protected readonly isEc3 = computed<boolean>(() => {
    return isEC3(this.criteria().ecNumber);
  });

  onSubCriteriaSelected(value: string | number): void {
    this.activeSubCriteriaTabIndex.set(
      typeof value === 'string' ? Number.parseInt(value, 10) : value,
    );

    logTabRender('SC', value);
  }

  onConsistencyChanged(change: AttributeConsistencyChange): void {
    this.consistencyChanged.emit(change);
  }

  onScoreSelected(selection: AttributeScoreSelection): void {
    const unsavedScores = new Map(this.unsavedScores());
    unsavedScores.set(selection.directusId, selection.score);
    this.unsavedScores.set(unsavedScores);

    this.unsavedScoresChanged.emit(true);
  }

  async onSave(subCriteriaId: number): Promise<void> {
    const subCriteria = this.criteria().subCriteria.find((item) => item.id === subCriteriaId);

    if (subCriteria === undefined) {
      return;
    }

    const changedAttributes = this.applyEdits(subCriteria, this.unsavedScores()).filter(
      (attribute) => this.unsavedScores().has(attribute.directusId),
    );

    if (changedAttributes.length === 0) {
      return;
    }

    this.savingSubCriteriaId.set(subCriteriaId);

    try {
      const response: EvaluationSubCriteriaAttributeResponse = await firstValueFrom(
        this.evaluationService.saveSubCriteriaAttributeEvaluations(
          this.projectId(),
          this.criteria().id,
          subCriteriaId,
          this.saveService.buildSaveRequest(
            changedAttributes.map((attribute) => ({
              id: attribute.id,
              directusId: attribute.directusId,
              scaNumber: attribute.scaNumber,
              selectedImportanceScore: attribute.score,
            })),
          ),
          TenderPhaseType.RFP,
        ),
      );

      this.lastSaveAuditInfo.set(response.auditInfo ?? null);
      this.discardEdits(changedAttributes);
      this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
      this.saved.emit({
        evaluationCriteriaId: this.criteria().id,
        subCriteriaId: subCriteriaId,
        attributes: response.attributes,
      });
    } finally {
      this.savingSubCriteriaId.set(undefined);
    }
  }

  private applyEdits(
    subCriteria: SubCriteriaTabVM,
    unsavedScores: Map<string, EvaluationSubCriteriaImportanceScore | undefined>,
  ): AttributeScoreVM[] {
    return subCriteria.attributes.map((attribute) => ({
      ...attribute,
      score: unsavedScores.has(attribute.directusId)
        ? unsavedScores.get(attribute.directusId)
        : attribute.score,
    }));
  }

  private discardEdits(savedAttributes: AttributeScoreVM[]): void {
    const unsavedScores = new Map(this.unsavedScores());
    savedAttributes.forEach((attribute) => unsavedScores.delete(attribute.directusId));
    this.unsavedScores.set(unsavedScores);

    this.unsavedScoresChanged.emit(unsavedScores.size > 0);
  }
}
